/**
 * Offers the guided wizard on a story that has not got going yet.
 *
 * The workspace behind it is already usable — manuscript in the middle, chat open on the
 * right — so every way out of this dialog is a valid way to begin. Dismissing is not a
 * decision deferred; it is the writer choosing to just write. Nothing dims the workspace
 * for the same reason: the thing being weighed against the wizard is right there.
 */
import React from 'react'
import { WandSparkles } from 'lucide-react'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { Card } from '@astryxdesign/core/Card'
import PillButton from './PillButton'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Heading, Text } from '@astryxdesign/core/Text'

export interface WizardOfferModalProps {
  onAccept: () => void
  onDismiss: () => void
}

export default function WizardOfferModal({
  onAccept,
  onDismiss
}: WizardOfferModalProps): React.JSX.Element {
  return (
    <Dialog
      isOpen
      onOpenChange={(open: boolean) => !open && onDismiss()}
      variant="standard"
      // 'info', not 'form': there is nothing here to lose, so a backdrop click dismissing
      // the offer is the right outcome rather than an accident to guard against.
      purpose="info"
      width={520}
      className="wizard-offer-dialog"
    >
      <DialogHeader
        title="Want a hand getting started?"
        onOpenChange={() => onDismiss()}
        className="wizard-offer-header"
      />
      <VStack gap={3}>
        {/* Presentation only — the primary button below is the action. A card that were
            itself clickable would put two different-looking targets on one choice. */}
        {/* The class is the guided tour's last anchor -- it spotlights this card from
            inside the dialog (see editorTour.ts). */}
        <Card padding={4} className="wizard-offer-card">
          <VStack gap={2} align="start">
            <HStack gap={2} vAlign="center">
              <WandSparkles size={20} className="text-primary" />
              <Heading level={2}>Guided wizard</Heading>
            </HStack>
            <Text type="body" color="secondary">
              Answer a few quick questions — genre, tone, characters, narrative perspective, and
              setting — and we&apos;ll turn your answers into a story bible and an opening scene.
            </Text>
          </VStack>
        </Card>
        {/* The header's X carries aria-label="Close" too, so these two share a name. That is
            the usual dialog pairing and they do the same thing; the class is what lets a test
            say which one it means. */}
        <HStack gap={2} justify="end" className="wizard-offer-actions">
          <PillButton variant="secondary" label="Close" onClick={onDismiss} />
          <PillButton variant="primary" label="Guided Wizard" onClick={onAccept} />
        </HStack>
      </VStack>
    </Dialog>
  )
}
